
import { substanceCodetableIds } from 'app/area/codetable/item/constants';
import { ScreenField, ScreenPicker } from 'app/area/codetable/item/screen-renderer/types';
import {
  CodeActionType, ReadOnlyType, InitializeCodeItemPayload, ReadOnlyConfigurationValue,
  ReadOnlyConfiguration, SubstanceUomSetType, CodeItemAccessConfiguration, UomSetValidation
} from 'app/area/codetable/item/types';
import { AccessCodes, CodetableAccess } from 'app/area/codetable/types-store';
import { CodeComplexValue } from 'app/area/shared/codetable/types';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { ValidationProps } from 'ui';

export const handleCreateCodeItem = (payload: InitializeCodeItemPayload): void => {
  shouldCreateNewCodeItem(payload) && payload.createCodeItem(payload);
  shouldBlockAccess(payload) && payload.codetableBlockAccess(payload?.breadcrumbs);
};

export const handleLoadCodeItem = (payload: InitializeCodeItemPayload, previousPayload: InitializeCodeItemPayload): void => {
  const { codetableId, item: { fetchingCodeId }, loadCodeItem, clearValidation } = payload;

  const accessCodes = payload && previousPayload && accessCodesLoaded(payload, previousPayload);
  const payloadChanged = payload && previousPayload && (isDefinedIncludingZero(codetableId) && fetchingCodeId === undefined
    && hasPayloadChanged(payload, previousPayload));

  if (accessCodes || payloadChanged) {
    clearValidation();
    loadCodeItem(payload);
  }
};

const shouldCreateNewCodeItem = (payload: InitializeCodeItemPayload) => {
  const { codeId } = payload;
  return !codeId && codeId !== 0;
};

const shouldBlockAccess = (payload: InitializeCodeItemPayload) => {
  return (payload.actionType === CodeActionType.Add || payload.actionType === CodeActionType.Duplicate)
    && (!payload?.access?.data || !payload?.access?.data?.codetables?.create);
};

const hasPayloadChanged = (payload: InitializeCodeItemPayload, previousPayload: InitializeCodeItemPayload) => {
  const { codetableId, codeId, parentId, actionType } = payload;
  return codetableId !== previousPayload.codetableId || codeId !== previousPayload.codeId
    || parentId !== previousPayload.parentId || actionType !== previousPayload.actionType;
};

const accessCodesLoaded = (payload: InitializeCodeItemPayload, previousPayload: InitializeCodeItemPayload) => {
  const { access: { data: { codes: currAccessCodes } = {} } = {} } = payload;
  const { access: { data: { codes: prevAccessCodes } = {} } = {} } = previousPayload;

  return !prevAccessCodes && !!currAccessCodes;
};

export const canLoadCodesAccess = ({ canCreate, administrationCentral }) => (!canCreate && !administrationCentral) || administrationCentral;

export const readOnlyConfiguration = (value: ReadOnlyConfigurationValue): ReadOnlyConfiguration => {

  if (!value) {
    return undefined;
  }

  const { actionType, codeId, administrationCentral, canCreate, canEdit, canMove, readonlySubsets,
    codetableMetadata: { features: { description: translationsDescription } = {} } = {} } = value;
  const isNotSelected = codeId === 0;
  const emptySubsets = !readonlySubsets || (!!readonlySubsets && readonlySubsets.length === 0);
  const edit = administrationCentral || canEdit;

  const noSubsetsNoEdit = emptySubsets && !edit;
  const notSelectedWithoutAdminCentral = isNotSelected && !administrationCentral;
  const notSelectedWithoutMetaDescription = isNotSelected && edit && !translationsDescription;
  const notSelectedWithMetaDescription = isNotSelected && canEdit && canCreate && !administrationCentral && translationsDescription;

  return {
    [CodeActionType.Add]: {
      [ReadOnlyType.save]: !canCreate,
      [ReadOnlyType.all]: !canCreate
    },
    [CodeActionType.Duplicate]: {
      [ReadOnlyType.save]: !canCreate,
      [ReadOnlyType.all]: !canCreate
    },
    [CodeActionType.Edit]: {
      [ReadOnlyType.save]: !notSelectedWithMetaDescription
        && (noSubsetsNoEdit || notSelectedWithoutAdminCentral || notSelectedWithoutMetaDescription),
      [ReadOnlyType.all]: !edit || isNotSelected,
      [ReadOnlyType.subsets]: (emptySubsets && !edit) || isNotSelected,
      [ReadOnlyType.translations]: !edit,
      [ReadOnlyType.codeParent]: !(administrationCentral || canMove)
    }
  }[actionType];
};

export const createUomSetFieldId = (fieldName: ScreenPicker, uomSetId: number) => `${fieldName}_${uomSetId}`;

export const createConversionFactorsId = (fieldName: (ScreenPicker | ScreenField), uomConversionTypeId: number) =>
  `${fieldName}_${uomConversionTypeId}`;

export const substanceUomSetType = (codetableId: number): SubstanceUomSetType => {
  return {
    [substanceCodetableIds.hierarchy]: SubstanceUomSetType.substanceUomSet,
    [substanceCodetableIds.category]: SubstanceUomSetType.substanceCategoryUomSet
  }[codetableId];
};

export const codeAccessConfiguration = (
  codetableId: number,
  access: CodetableAccess,
  listItemAccess: AccessCodes
): CodeItemAccessConfiguration => {

  const canCreate = !!(access && access.codetables && access.codetables.create);
  const canDelete = !!(access && access.codes && access.codes.delete);
  const canDeleteOnDemand = !!(listItemAccess && listItemAccess.delete);
  const isCentralAdmin = !!(access && access.codetables && access.codetables.administrationCentral);

  return {
    canCreate,
    canDuplicate: canCreate && codetableId !== substanceCodetableIds.uomSets,
    canDelete: !!(canDelete || canDeleteOnDemand || (canCreate && isCentralAdmin)),
    isCentralAdmin
  };
};

export interface MultipleValuesValidation {
  existingItems: CodeComplexValue<number>[];
  newItems: CodeComplexValue<number>[];
  validationKey: string;
  validation: { [key: string]: ValidationProps };
}

export const clearValidationForNewItems = ({ existingItems = [], newItems = [], validation, validationKey }: MultipleValuesValidation) => {
  if (!validation?.[validationKey]) {
    return validation;
  }

  const difference = newItems.filter(item => !!existingItems.find(compared => item.value === compared.value)) ||
    existingItems.filter(item => !!newItems.find(compared => item.value === compared.value));

  const messages = validation[validationKey]?.messages?.filter(message => !!difference.find(item => item.value === message.parameters.id))
    || [];

  return {
    ...validation,
    [validationKey]: messages.length > 0 ? {
      ...validation[validationKey],
      messages: messages
    } : undefined
  };
};

export const getUomSetValidation = ({ uomSet, uomTypeName, uomTypeValue, validation }: UomSetValidation): ValidationProps => {
  const messages = validation?.messages?.filter(message =>
    message?.parameters?.[uomTypeName] === uomTypeValue && message?.parameters?.uomSet === uomSet);
  return messages?.length > 0 ? { ...validation, messages } : undefined;
};
